<template>
  <div id="SessionViewerUserHistory">
    <LoadingLayout v-if="dataLoading"/>
    <template v-else>
      <Card class="title-card">
        <CardSection>
          <ButtonGroup>
            <Button @click="viewSummary = true">{{$t('SessionViewer.showUserDetails')}}</Button>
            <ButtonGroupItem style="margin-left: auto">
              <Button @click="$emit('reverse-timeline')"><i class="fas fa-sort"></i></Button>
            </ButtonGroupItem>
          </ButtonGroup>
          
          <Filters
            class="filter"
            :query.sync="query"
          />
        </CardSection>
      </Card>
      <template v-if="formattedHistory && formattedHistory.length">
        <div class="timeline-date">
          <Formatter type="dateTimeLong" :value="firstHistory.date"/>
        </div>
        <div class="timeline">
          <div v-for="(item, index) in formattedHistory" :key="index">
            <!-- ${index % 2 === 0 ? `page-left` : `page-right`}` -->
            <div :class="`container ${index % 2 === 0 ? `page-left` : `page-right`}`"> 
              <div v-if="item.historyType === 'visit'" class="page-content">
                <div>
                  <div class="history-type">
                    {{$t('Visit')}}
                  </div>
                  <div class="index">
                    #{{item.index + 1}}
                  </div>
                </div>
                <hr class="hr-line">
                <div class="section-content">
                  <Stat :label="$t('SessionViewer.Session Key')">{{item.sk}}</Stat>
                  <Button class="to-session-button" size="small" @click.prevent="goToSession(item.sk)">{{$t('SessionViewer.goToSession')}}</Button>
                </div>
                <hr class="hr-line">
                <div class="section-content">
                  <Stats>
                    <Stat :label="$t('Date')"><Formatter type="dateTimeShort" :value="item.date"/></Stat>
                    <Stat :label="$t('SessionViewer.fpConversion')" :value="item.isFP"/>
                    <Stat :label="$t('SessionViewer.moneySpent')"><Formatter type="currency" :currency="currency" :value="item.spend"/></Stat>
                    <Stat :label="$t('Discount')"><Formatter type="currency" :currency="currency" :value="item.discount"/></Stat>
                    <Stat :label="$t('SessionViewer.numberOfPages')" :value="item.numPages"/>
                    <Stat :label="$t('Duration')">
                      <Formatter v-if="item.duration" type="durationMs" :value="item.duration"/>
                      <template v-else>—</template>
                    </Stat>
                  </Stats>
                </div>
                <!-- <pre>{{item}}</pre> -->
              </div>
              <div v-else-if="item.historyType === 'currentVisit'" class="page-content">
                <div>
                  <div class="history-type">
                    {{$t('Current Visit')}}
                  </div>
                  <div class="index">
                    #{{item.index + 1}}
                  </div>
                </div>
                <hr class="hr-line">
                <div class="section-content">
                  <Stat :label="$t('SessionViewer.Session Key')">{{item.sk}}</Stat>
                  <Button class="to-session-button" size="small" @click.prevent="goToSession(item.sk)">{{$t('SessionViewer.goToSession')}}</Button>
                </div>
              </div>
              <div v-else class="page-content">
                <div>
                  <div class="time-header">
                    <Formatter type="dateTimeShort" :value="item.date"/>
                  </div>
                  <div class="history-type">
                    {{$t('Notification')}}
                  </div>
                  <div class="index">
                    #{{item.index + 1}}
                  </div>
                </div>
                <hr class="hr-line">
                <div class="section-content">
                  <Stats>
                    <Stat :label="$t('Name')" :value="item.name"/>
                    <Stat :label="$t('Type')" :value="item.type"/>
                    <Stat :label="$t('Status')" :value="item.status"/>
                    <Stat :label="$t('SessionViewer.Hint')" :value="item.hint"/>
                  </Stats>
                </div>
                <!-- <pre>{{item}}</pre> -->
              </div>
            </div> 
          </div>
        </div>
        <div class="timeline-date">
          <Formatter type="dateTimeLong" :value="lastHistory.date"/>
        </div>
      </template>
      <Sheet
        :title="$t('Summary')"
        :width="700"
        :visible="viewSummary"
        @close="viewSummary = false"
        padded
      >
        <template>
          <Card v-if="mostRecentSessionKey !== session.sessionKey">
            <CardSection>
              <Checkbox :label="$t('SessionViewer.useRecentSessionMessage')" v-model="useMostRecentSession"/>
            </CardSection>
          </Card>
          <LoadingLayout v-if="sessionLoading" style="padding-top: 20px;"/>
          <template v-else-if="!useMostRecentSession || recentSession">
            <Card :title="useMostRecentSession ? $t('SessionViewer.mostRecentSessionUserSummary') : $t('SessionViewer.currentSessionUserSummary')">
              <CardSection>
                <Stats>
                  <Stat
                    :label="$t('User Key')"
                    :value="`${userDetailsSession.userKey} ${userDetailsSession.userSite.userId ? `(${userDetailsSession.userSite.userId})` : ''}`"
                  />
                  <Stat 
                    :label="$t('SessionViewer.Location')"
                    :value="`${userDetailsSession.city ? userDetailsSession.city + ', ' : ' '}${userDetailsSession.region ? userDetailsSession.region + ', ' : ' '}${userDetailsSession.country}`"
                  />
                  <Stat
                    :label="$t('Timezone')"
                    :value="formatTimezone(userDetailsSession.timeZone)"
                  />
                  <Stat
                    :label="$t('User Agent')"
                  >{{ userDetailsSession.userAgent }}</Stat>
                  <Stat
                    :label="$t('IP Address')"
                    :value="userDetailsSession.ipAddress"
                  />
                  <Stat
                    :label="$t('Customer ID')"
                    :value="userDetailsSession.lookupMap ? userDetailsSession.lookupMap.CUSTOMER_ID : null"
                  />
                  <Stat
                    v-if="userDetailsSession.userSite && userDetailsSession.userSite.ignoreUserUntil"
                    :label="$t('SessionViewer.Ignore Until')"
                    :value="`${convertHxDate(userDetailsSession.userSite.ignoreUserUntil)} ${isIgnoring ? `(${$t('SessionViewer.Currently Being Ignored')})` : `${$t('SessionViewer.Ignore Period Ended')}`}`"
                  />
                  <Stat 
                    v-else
                    :label="$t('SessionViewer.Ignore Until')"
                    :value="$t('N/A')"
                  />
                </Stats>
              </CardSection>
            </Card>
            <Card :title="$t('SessionViewer.sessionHistorySummary')">
              <CardSection v-if="history && history.userSite">
                <Stats>
                  <Stat :label="$t('SessionViewer.First Seen')" type="dateTimeLong" :value="convertDate(history.userSite.firstTracked)"/>
                  <Stat :label="$t('SessionViewer.Last Seen')" type="dateTimeLong" :value="convertDate(history.userSite.lastTracked)"/>
                  <Stat :label="$t('SessionViewer.Total Spend')" type="currency" :currency="currency" :value="history.userSite.totSpend"/>
                  <Stat :label="$t('SessionViewer.Total Discount')" type="currency" :currency="currency" :value="history.userSite.totDiscount"/>
                  <Stat :label="$t('SessionViewer.Total Visits')" :value="history.userSite.totVisits"/>
                  <Stat :label="$t('SessionViewer.Total Offers')" :value="history.userSite.totOffers"/>
                </Stats>
              </CardSection>
            </Card>
            <Card :title="useMostRecentSession ? $t('SessionViewer.mostRecentSessionSubscriptionStatus') : $t('SessionViewer.currentSessionSubscriptionStatus')">
              <CardSection>
                <table class="table table-bordered">
                  <tbody>
                    <tr>
                      <th width="200">{{$t('Supported')}}</th>
                      <td><Stat :value="userDetailsSession.pushInfo.isSupported ? $t('Yes') : $t('No')"/></td>
                    </tr>
                    <tr>
                      <th width="200">{{$t('Permission')}}</th>
                      <td><Stat :value="userDetailsSession.pushInfo.permission"/></td>
                    </tr>
                    <tr>
                      <th width="200">{{$t('Subscription')}}</th>
                      <td v-if="userDetailsSession.pushInfo.subscription"><pre style="max-width: 450px; overflow-x: scroll;">{{ (userDetailsSession.pushInfo.subscription) }}</pre></td>
                      <td v-else><Stat/></td>
                    </tr>
                  </tbody>
                </table>
              </CardSection>
            </Card>
          </template>
        </template>
      </Sheet>
    </template>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'SessionViewerUserHistory',
  props: {
    sessionFlow: {
      type: Object,
      required: true
    },
    history: {
      type: Object,
      required: true
    },
    notifications: {
      type: Object,
      required: true
    },
    dataLoading: {
      type: Boolean,
      required: true
    },
    currency: {
      type: String,
      required: true
    },
    reverse: {
      type: Boolean,
      required: true
    },
    fromSession: {
      type: Object
    },
    viewSummary: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      offset: 0,
      query: null,
      sortBy: {
        column: 'date',
        direction: 'desc'
      },
      useMostRecentSession: false,
      recentSession: null,
      sessionLoading: false
    }
  },
  mounted() {
    this.$watch('useMostRecentSession', (val) => {
      if (val) {
        this.getMostRecentSession()
      }
    })
  },
  methods: {
    getMostRecentSession() {
      if (!this.recentSession) {
        this.sessionLoading = true
        this.api.getSessionDetails(this.mostRecentSessionKey, false)
          .then((data) => {
            this.recentSession = data.session
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.sessionLoading = false
          })
      }
    },
    convertDate(date) {
      return date && window.moment.tz(date, 'GMT')
        .tz(this.theUser.accountTimezone)
        .format('YYYY-MM-DD HH:mm:ss')
    },
    convertHxDate(time) {
      if (time && time.__t) {
        const date = window.moment(time.__t * 1000).format('YYYY-MM-DD HH:mm:ss')
        return window.moment(date)
                .utcOffset(this.session.timeZone)
                .tz(this.theUser.accountTimezone)
                .format('YYYY-MM-DD HH:mm:ss')
      }
      return ''
    },
    formatTimezone(offset) {
      const hours = parseInt(offset) / -60
      return hours > 0 ? `UTC+${hours}:00` : `UTC-${Math.abs(hours)}:00`
    },
    goToSession(sk) {
      if (sk === this.session.sessionKey) {
        this.$emit('back')
      } else {
        this.$emit('refresh-session', sk)
      }
      // this.$router.go()
    }
  },
  computed: {
    session() {
      return this.sessionFlow && this.sessionFlow.session
    },
    formattedHistory() {
      // console.log(this.history)
      let history = this.history && this.history.userSite && this.history.userSite.visits ? this.history.userSite.visits.map(v => {
        return {
          ...v,
          historyType: 'visit',
          date: this.convertDate(v.end),
          duration: v.duration * 1000,
          isFP: v.isFPConversion ? this.$t('Yes') : this.$t('No')
        }
      }) : []
      if (this.notifications) {
        history.push(...this.notifications.map(n => {
          return {
            ...n,
            historyType: 'notification',
            date: n.sentOn
          }
        }))
      }

      // add current session if its not in the list already
      const currSession = history.find(h => h.sk === this.session.sessionKey)
      if (!currSession) {
        history.push({
          sk: this.session.sessionKey,
          historyType: 'currentVisit',
          date: this.convertHxDate(this.session.lastAccessedTime)
        })
      } else if (this.fromSession && !history.find(h => h.sk === this.fromSession.sessionKey)) {
        history.push({
          sk: this.fromSession.sessionKey,
          historyType: 'currentVisit',
          date: this.fromSession.date
        })
      }

      if (this.sortBy) {
        history = _.orderBy(history, [this.sortBy.column], [this.sortBy.direction])
      }
      if (this.query) {
        history = history.filter(item => JSON.stringify(item).toLowerCase().indexOf(this.query.toLowerCase()) >= 0)
      }
      history = history.map((h, index) => {
        return {
          ...h,
          index
        }
      })
      if (this.reverse) {
        return [...history].reverse()
      }
      return history
    },
    mostRecentSessionKey() {
      const visitHistory = this.formattedHistory.filter(h => h.historyType === 'currentVisit' || h.historyType === 'visit')
      if (!visitHistory || !visitHistory.length) return null
      return visitHistory[0].sk
    },
    userDetailsSession() {
      return this.useMostRecentSession ? this.recentSession : this.session
    },
    firstHistory() {
      return this.formattedHistory && this.formattedHistory.length ? this.formattedHistory[0] || {} : {}
    },
    lastHistory() {
      return this.formattedHistory && this.formattedHistory.length ? this.formattedHistory[this.formattedHistory.length - 1] || {} : {}
    }
  }
}
</script>

<style lang="scss">
#SessionViewerUserHistory {
  /* The actual timeline (the vertical ruler) */
  .timeline {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
  }
  // .timeline > div {
  //   margin-bottom: -20px;
  // }
  .timeline > div:first-of-type .page-content {
    margin-top: 25px;
  }
  .timeline > div:last-of-type .page-content {
    margin-bottom: 25px;
  }
  .timeline > div:first-of-type .container::before {
    top: 40px !important;
  }
  .timeline > div:first-of-type .page-left::after {
    top: 40px !important;
  }

  /* The actual timeline (the vertical ruler) */
  .timeline::after {
    content: '';
    position: absolute;
    width: 4px;
    background-color: #FF9800;
    top: 5px;
    bottom: 5px;
    left: 50%;
    margin-left: -3px;
  }
  /* Container around content */
  .container {
    padding: 5px 40px;
    position: relative;
    background-color: inherit;
    width: 50%;
    left: 50%;
  }

  /* The circles on the timeline */
  .container::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background-color: #363C4B;
    border: 3px solid #EEEEEE;
    top: 20px;
    border-radius: 50%;
    z-index: 1;
    right: -9px;
  }


  /* The actual content */
  .page-content {
    // padding: 10px 15px;
    background-color: #FFFFFF;
    position: relative;
    border-radius: 3px;
    // border: 2px solid #E6DFDF;
    box-shadow: 0 0 0 1px rgba(63,63,68,.05), 0 1px 3px 0 rgba(63,63,68,.15);
    // transition: transform .2s;
    // &:hover {
    //   transform: scale(1.02);
    // }
    // cursor: pointer;
    a {
      display: block;
      width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }


  /* Place the container to the left */
  .page-left, .not-page-left {
    left: 0;
  }

  /* Place the container to the right */
  .page-right, .not-page-right {
    left: 50%;
  }

  /* Add arrows to the left container (pointing right) */
  .page-left::before {
    content: " ";
    height: 0;
    position: absolute;
    top: 21px;
    width: 0;
    z-index: 0;
    right: 30px;
    border: medium solid #E6DFDF;
    border-width: 10px 0 10px 10px;
    border-color: transparent transparent transparent #E6DFDF;
  }

  /* Add arrows to the right container (pointing left) */
  .page-right::before {
    content: " ";
    height: 0;
    position: absolute;
    top: 21px;
    width: 0;
    z-index: 0;
    left: 30px;
    border: medium solid #E6DFDF;
    border-width: 10px 10px 10px 0;
    border-color: transparent #E6DFDF transparent transparent;
  }

  /* Fix the circle for containers on the right side */
  .page-right::after {
    left: -11px;
  }

  .timeline-date {
    // display: inline-block;
    position: relative;
    text-align: center;
    margin: 0 auto;
    width: 225px;
    background-color: #363C4B;
    z-index: 1;
    padding: 5px 15px;
    color: white;
    border-radius: 2px;
  }

  .history-type {
    // text-align: center;
    padding-top: 7px;
    margin: 7px 50px;
  }

  .index {
    position: absolute;
    top: 0px;
    left: 0px;
    padding: 7px;
    background-color:#FF9800;
    color: #FFFFFF;
    border-radius: 2px 0px 0px 0px;
  }
  .time-header {
    position: absolute;
    right: 0px;
    top: 0px;
    padding: 7px;
    // background-color:#FF9800;
    // color: #FFFFFF;
    border-radius: 0px 2px 0px 0px;
  }
  .section-content {
    padding: 10px;
  }

  .to-session-button {
    position: absolute;
    right: 10px;
    top: 45px;
  }

  .hr-line {
    background-color: #E6DFDF;
    border: 1px solid #E6DFDF;
    margin: 0px;
  }

  .title-card {
    margin-bottom: 20px;
  }

  .filter {
    padding-bottom: 0px;
    border-bottom: none;
  }



  @media screen and (max-width: 1100px) {
    .container {
      width: 100%;
      padding: 5px 0px;
      z-index: 1;
    }
    .page-right {
      left: 0;
    }
    .page-left::before, .page-left::after, .page-right::before, .page-right::after {
      display: none;
    }
    .timeline::after {
      left: 50%;
    }
    .timeline-date {
      display: block;
      width: 225px;
      margin: 0 auto;
    }
  }
}
</style>